import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '../../../components/ui/Drawer';
import TabBar from '../../../components/TabBar';
import { useDrawerResize } from '../../../hooks/useDrawerResize';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { PermissionAccessProvider, PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import safetySystemService from '../../../services/safetySystem.service';
import type { MaintenanceDefectSection, MaintenanceDetail, MaintenanceSaveBody } from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import { DateField, Field, Section, SelectField, TextField, YesNoField, inputClass, str } from './unitFields';
import { isDirty } from './unitForm';
import { DriverTypeahead, driverLabel } from './ticketTypeaheads';
import { DiscardFooter, ReadOnly, ResizeHandle, SafetyHistoryTab, errorText, fmtDateTime } from './safetyDrawerShared';
import {
  DefectState, MONTH_OPTIONS, STATUS_TONE_CLASS, buildReportBody, changedDefects, defectState, defectsDirty, isApproved, monthName,
  newReportDefaults, pickReportFields, statusTone, validateMonthYear, yearOptions,
} from './maintenanceForm';
import { shortDate } from '../SafetyUnits.columns';

/**
 * Maintenance Report drawer — port of the V1 Safety monthly maintenance report page.
 * Two tabs: Report (one scrolling body: report fields, tickets for the month, repairs,
 * inspection defects, corrections) and History. No Documents tab for the report itself
 * (V1 parity): documents hang off the repair and inspection-defect rows.
 *
 * `recno === null` is a brand-new report; the first save creates it and the drawer stays
 * open on the created recno. Save posts `{ report: <changed keys> (+ approve) }` when the
 * report changed, then the changed checklist rows (the same call the Save Defects button
 * makes), then closes; a checklist failure keeps the drawer open on the error. Corrections
 * Needed and Delete post their controls on their own. A 409 dupe shows inline (no bypass);
 * 422 inline.
 */

export interface MaintenanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  recno: number | null;
  onSaved: () => void;
}

type TabId = 'report' | 'history';

/** `maintenancereport_history.dbtable` -> display label. */
const HISTORY_LABELS: Record<string, string> = { maintenancereport: 'Report', inspectiondefect: 'Defect', maintrepair: 'Repair' };

interface Dupe { recno: number; unitnumber: string; maintmonthname: string; maintyear: number | string; maintstatus: string }

const newDetail = (): MaintenanceDetail => ({
  report: { ...newReportDefaults(), maintstatus: '', maintapprovedatetime: '', drivername: '', maintvin: '' },
  tickets_for_month: [], repairs: [], defects: [], corrections: [],
});

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1.5 py-1 align-top';

const Block: React.FC<{ title: string; children: React.ReactNode; actions?: React.ReactNode }> = ({ title, children, actions }) => (
  <section className="rounded-md border border-rule p-2">
    <div className="mb-1 flex items-center justify-between gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      {actions}
    </div>
    {children}
  </section>
);

const Docs = ({ tablename, tablerecno, label }: { tablename: 'maintrepair' | 'inspectiondefect'; tablerecno: number; label: string }) => (
  <details className="mt-1">
    <summary className="cursor-pointer text-xs text-accent-text hover:underline">{label}</summary>
    <div className="mt-1"><SafetyDocuments tablename={tablename} tablerecno={tablerecno} isReadOnly /></div>
  </details>
);

export default function MaintenanceDrawer({ isOpen, onClose, recno: recnoProp, onSaved }: MaintenanceDrawerProps) {
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;
  const { width, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen, storageKey: 'safetyMaintenanceDrawerWidth', minWidth: 480, defaultWidth: 1100,
  });

  // The recno the drawer is showing: the prop, or the recno a create just produced.
  const [recno, setRecno] = useState<number | null>(recnoProp);
  useEffect(() => { setRecno(recnoProp); }, [recnoProp, isOpen]);

  const [detail, setDetail] = useState<MaintenanceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('report');

  // Editable copies + the pristine originals (in `detail`) they are diffed against on Save.
  const [report, setReport] = useState<Record<string, any>>({});
  const [approved, setApproved] = useState(false);
  const [driverText, setDriverText] = useState('');
  const [defects, setDefects] = useState<DefectState>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dupes, setDupes] = useState<Dupe[] | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const [savingDefects, setSavingDefects] = useState(false);
  const [defectsError, setDefectsError] = useState('');
  const [defectsSaved, setDefectsSaved] = useState('');

  // Inline reason prompts: corrections / delete.
  const [prompt, setPrompt] = useState<'corrections' | 'delete' | null>(null);
  const [reason, setReason] = useState('');
  const [promptBusy, setPromptBusy] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen && contentRef.current) setPortalContainer(contentRef.current.closest('dialog') || null);
  }, [isOpen, detail]);

  const resetForm = useCallback((d: MaintenanceDetail | null) => {
    setReport(d ? pickReportFields(d.report) : {});
    setApproved(d ? isApproved(d.report.maintapprovedatetime) : false);
    setDriverText(d && Number(d.report.driverrecno) > 0 ? driverLabel({ unitnumber: d.report.driverunitnumber ?? d.report.unitnumber, drivername: d.report.drivername }) : '');
    setDefects(d ? defectState(d.defects ?? []) : {});
    setSaveError('');
    setDupes(null);
    setDefectsError('');
    setDefectsSaved('');
    setConfirmDiscard(false);
    setPrompt(null);
    setReason('');
  }, []);

  const load = useCallback((id: number, signal?: AbortSignal) => {
    setLoading(true);
    setLoadError('');
    return safetySystemService.getMaintenance(id, signal)
      .then((d) => {
        if (signal?.aborted) return;
        setDetail(d);
        resetForm(d);
      })
      .catch((e) => {
        if (signal?.aborted) return;
        setDetail(null);
        setLoadError(e?.response?.data?.detail || 'Unable to load this maintenance report.');
      })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [resetForm]);

  // Load on open / recno change. A null recno is a new report: no fetch, blank form.
  useEffect(() => {
    if (!isOpen) {
      setDetail(null);
      resetForm(null);
      return;
    }
    setActiveTab('report');
    if (recno === null) {
      const d = newDetail();
      setDetail(d);
      resetForm(d);
      setLoading(false);
      setLoadError('');
      return;
    }
    const controller = new AbortController();
    load(recno, controller.signal);
    return () => controller.abort();
  }, [isOpen, recno, load, resetForm]);

  const isNew = recno === null;
  const baseReport = useMemo(() => (detail ? pickReportFields(detail.report) : {}), [detail]);
  const baseApproved = detail ? isApproved(detail.report.maintapprovedatetime) : false;
  const isDeleted = str(detail?.report?.deleted).toUpperCase() === 'Y';
  const reportDirty = useMemo(() => !!detail && (isDirty(baseReport, report) || approved !== baseApproved), [detail, baseReport, report, approved, baseApproved]);
  const checklistDirty = useMemo(() => !!detail && defectsDirty(detail.defects ?? [], defects), [detail, defects]);
  const dirty = reportDirty || checklistDirty;

  const clearErrors = () => { setSaveError(''); setDupes(null); };
  const onReportChange = (key: string, value: unknown) => { setReport((p) => ({ ...p, [key]: value })); clearErrors(); };
  const t = (key: string) => (v: unknown) => onReportChange(key, v);

  /**
   * PUT the changed checklist rows and make them the new baseline (the report form is left
   * untouched). Throws on failure after setting the checklist error, so Save can keep the
   * drawer open. Returns false when nothing changed.
   */
  const saveDefectRows = async (): Promise<boolean> => {
    if (!detail || recno === null) return false;
    const rows = changedDefects(detail.defects ?? [], defects);
    if (!rows.length) return false;
    setSavingDefects(true);
    setDefectsError('');
    setDefectsSaved('');
    try {
      const res = await safetySystemService.saveDefects(recno, rows);
      const applied: MaintenanceDefectSection[] = (detail.defects ?? []).map((s) => ({
        ...s,
        defects: s.defects.map((d) => ({ ...d, isdefect: defects[d.defectrecno]?.isdefect ?? d.isdefect, defectcomments: defects[d.defectrecno]?.defectcomments ?? d.defectcomments })),
      }));
      setDetail((prev) => (prev ? { ...prev, defects: applied, report: { ...prev.report, numdefects: res.numdefects } } : prev));
      setDefectsSaved(`Defects saved (${res.numdefects} flagged).`);
      return true;
    } catch (e: any) {
      setDefectsError(errorText(e));
      throw e;
    } finally {
      setSavingDefects(false);
    }
  };

  /**
   * Save: the report diff (when it changed), then the changed checklist rows (when they
   * changed), then close. Either part alone is enough to make Save do work -- checklist-only
   * edits used to fall through to a silent close. A checklist failure after a committed
   * report save re-baselines the report (so a retry only re-sends the checklist) and keeps
   * the drawer open on the error.
   */
  const handleSave = async () => {
    if (!detail || activeTab !== 'report') return;
    if (!isNew && !dirty) { onClose(); return; }
    const err = validateMonthYear(report.maintmonth, report.maintyear);
    if (err) { setSaveError(err); return; }
    // A create posts every report field (the server's pre-save baseline is blank, so a diff
    // against the client defaults would drop the unchanged month / year); an edit posts the diff.
    const body: MaintenanceSaveBody = isNew ? { report: pickReportFields(report) } : buildReportBody(baseReport, report, baseApproved, approved);
    setSaving(true);
    clearErrors();
    let reportSaved = false;
    try {
      if (isNew) {
        const res = await safetySystemService.createMaintenance(body);
        onSaved();
        setRecno(res.recno);   // stay open on the created report so the checklist / history become available
        return;
      }
      if (reportDirty) {
        await safetySystemService.saveMaintenance(recno as number, body);
        reportSaved = true;
      }
      if (checklistDirty) await saveDefectRows();
      onSaved();
      onClose();
    } catch (e: any) {
      if (reportSaved) {
        onSaved();
        // The report is committed: make the posted values the baseline so a retry sends only the checklist.
        setDetail((prev) => (prev ? {
          ...prev,
          report: { ...prev.report, ...report, ...(approved !== baseApproved ? { maintapprovedatetime: approved ? new Date().toISOString() : '' } : {}) },
        } : prev));
        setSaveError(`Report saved, but the inspection defects were not: ${errorText(e)}`);
      } else {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d && Array.isArray(d.dupes)) setDupes(d.dupes as Dupe[]);
        setSaveError(errorText(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefects = async () => {
    try {
      if (await saveDefectRows()) onSaved();
    } catch {
      /* the checklist error is already showing */
    }
  };

  const handlePromptConfirm = async () => {
    if (recno === null || !prompt) return;
    const text = reason.trim();
    if (!text) return;
    const body: MaintenanceSaveBody = prompt === 'corrections'
      ? { report: { corrections_needed: { reason: text } } }
      : { report: { deleted: 'Y', delete_reason: text } };
    setPromptBusy(true);
    setSaveError('');
    try {
      await safetySystemService.saveMaintenance(recno, body);
      onSaved();
      if (prompt === 'delete') {
        onClose();
      } else {
        setPrompt(null);
        setReason('');
        await load(recno);
      }
    } catch (e: any) {
      setSaveError(errorText(e));
    } finally {
      setPromptBusy(false);
    }
  };

  const requestClose = (): boolean => {
    if (dirty) { setConfirmDiscard(true); return false; }
    return true;
  };
  const handleCancel = () => { if (requestClose()) onClose(); };
  const handleDiscard = () => { resetForm(detail); onClose(); };

  const onDriverPick = (hit: { driverrecno?: unknown; truckrecno?: unknown; unitnumber?: unknown; drivername?: unknown }) => {
    setReport((p) => ({
      ...p,
      driverrecno: Number(hit.driverrecno) || 0,
      truckrecno: Number(hit.truckrecno) || 0,
      unitnumber: str(hit.unitnumber) || p.unitnumber,
    }));
    setDriverText(driverLabel(hit));
    clearErrors();
  };

  const status = str(detail?.report?.maintstatus).toUpperCase();
  const unit = str(report.unitnumber ?? detail?.report?.unitnumber);
  const month = monthName(report.maintmonth) || str(detail?.report?.maintmonthname);
  const title = detail ? (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-lg font-semibold">
        {isNew ? 'New Maintenance Report' : `${unit} - ${month} MAINTENANCE REPORT`}
      </span>
      {!isNew && status && (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE_CLASS[statusTone(status)]}`}>{status}</span>
      )}
      {isDeleted && <span className="inline-flex items-center rounded-full bg-danger px-2.5 py-0.5 text-xs font-semibold text-danger-fg">DELETED</span>}
    </div>
  ) : (loading ? 'Loading report…' : 'Maintenance Report');

  const tabs = [
    { id: 'report', label: 'Report', current: activeTab === 'report' },
    { id: 'history', label: 'History', current: activeTab === 'history', disabled: isNew },
  ];

  const years = useMemo(() => {
    const opts = yearOptions();
    const y = str(report.maintyear);
    return y && !opts.some((o) => o.value === y) ? [{ value: y, label: y }, ...opts] : opts;
  }, [report.maintyear]);

  const footer = confirmDiscard ? (
    <DiscardFooter onDiscard={handleDiscard} onKeep={() => setConfirmDiscard(false)} />
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex gap-2">
        {canEdit && detail && !isNew && !isDeleted && activeTab === 'report' && (
          <>
            <button type="button" onClick={() => { setPrompt('corrections'); setReason(''); }} disabled={promptBusy}
              className="rounded-md border border-amber-500 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed dark:text-amber-200 dark:hover:bg-amber-950">
              Corrections Needed
            </button>
            <button type="button" onClick={() => { setPrompt('delete'); setReason(''); }} disabled={promptBusy}
              className="rounded-md border border-rule px-3 py-1.5 text-sm text-danger-fg hover:bg-highlight disabled:cursor-not-allowed">
              Delete Report
            </button>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={handleCancel} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Cancel</button>
        {canEdit && detail && activeTab === 'report' && !isDeleted && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
          >
            {saving ? 'Saving…' : isNew ? 'Create Report' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );

  const editable = canEdit && !isDeleted;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={requestClose}
      title={title}
      maxWidth="full"
      width={`${width}px`}
      contentClassName="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6"
      footer={footer}
    >
      <ResizeHandle width={width} onHandleMouseDown={onHandleMouseDown} onHandleDoubleClick={onHandleDoubleClick} />
      <div ref={contentRef}>
        <TabBar tabs={tabs} onTabChange={(id) => setActiveTab(id as TabId)} compact noBorder className="mb-4" />

        {loadError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{loadError}</div>}
        {saveError && (
          <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">
            <div>{saveError}</div>
            {dupes && dupes.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {dupes.map((d) => <li key={d.recno}>{`#${d.recno} ${d.unitnumber} ${d.maintmonthname} ${d.maintyear} (${d.maintstatus})`}</li>)}
              </ul>
            )}
          </div>
        )}

        {prompt && !loading && detail && (
          <div role="group" aria-label={prompt === 'corrections' ? 'Corrections needed' : 'Delete report'}
            className="mb-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-600 dark:bg-amber-950">
            <div className="mb-1 font-medium">
              {prompt === 'corrections' ? 'Reason for corrections (sent to the report as a note; approval and submit date are cleared)' : 'Reason for deleting this report'}
            </div>
            <textarea aria-label={prompt === 'corrections' ? 'Corrections reason' : 'Delete reason'} value={reason} rows={2}
              onChange={(e) => setReason(e.target.value)} className={`${inputClass} mb-2`} />
            <div className="flex gap-2">
              <button type="button" onClick={handlePromptConfirm} disabled={promptBusy || !reason.trim()}
                aria-label={prompt === 'corrections' ? 'Confirm corrections' : 'Confirm delete'}
                className={`rounded-md px-3 py-1 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 ${prompt === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                {promptBusy ? 'Saving…' : prompt === 'corrections' ? 'Confirm' : 'Delete'}
              </button>
              <button type="button" onClick={() => { setPrompt(null); setReason(''); }} className="rounded-md border border-rule px-3 py-1 text-sm hover:bg-highlight">Cancel</button>
            </div>
          </div>
        )}

        {loading && <div className="py-8 text-center text-sm text-muted">Loading report…</div>}

        {!loading && detail && activeTab === 'report' && (
          <PermissionAccessProvider accessLevel={editable ? 2 : 1}>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <Section title="Report">
                  <TextField label="Unit #" value={report.unitnumber} onChange={t('unitnumber')} maxLength={20} />
                  <Field label="Driver">
                    <DriverTypeahead ariaLabel="Driver" value={driverText} onChange={setDriverText} onPick={onDriverPick} />
                  </Field>
                  <ReadOnly label="VIN" value={detail.report.maintvin} />
                  <TextField label="Mileage" value={report.truckmileage} onChange={t('truckmileage')} type="number" />
                  <SelectField label="Month" value={report.maintmonth} onChange={t('maintmonth')} options={MONTH_OPTIONS} />
                  <SelectField label="Year" value={report.maintyear} onChange={t('maintyear')} options={years} />
                  <DateField label="Submit Date" value={report.maintsubmitdate} onChange={t('maintsubmitdate')} portalContainer={portalContainer} />
                  <YesNoField label="Fined" value={report.maintfined} onChange={t('maintfined')} />
                </Section>
                <Section title="Status">
                  <ReadOnly label="Report Status" value={isNew ? 'NEW' : status} />
                  <ReadOnly label="Defects Flagged" value={isNew ? '' : detail.report.numdefects} />
                  <ReadOnly label="Created" value={fmtDateTime(detail.report.createdatetime)} />
                  <ReadOnly label="Last Changed" value={fmtDateTime(detail.report.lastchangedatetime)} />
                  <ReadOnly label="Approved By" value={detail.report.maintapprovedby} />
                  <ReadOnly label="Approved" value={fmtDateTime(detail.report.maintapprovedatetime)} />
                  {!isNew && (
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                      <PermissionAwareInput type="checkbox" aria-label="Approve" checked={approved} className="h-4 w-4 rounded border-rule-strong"
                        onChange={(e) => { setApproved(e.target.checked); clearErrors(); }} />
                      Approve
                      <span className="text-xs text-muted">{baseApproved ? 'Uncheck to revoke approval' : 'Closes the report and notifies the driver'}</span>
                    </label>
                  )}
                </Section>
              </div>

              <Block title="Tickets for the month">
                {detail.tickets_for_month.length === 0 ? (
                  <div className="py-2 text-center text-sm text-muted">No tickets for this driver in the report month.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr><th className={th}>Ticket #</th><th className={th}>Date</th><th className={th}>Violation</th><th className={th}>Description</th><th className={th}>Unit</th><th className={th}>OOS</th></tr></thead>
                      <tbody>
                        {detail.tickets_for_month.map((r, i) => (
                          <tr key={`${r.ticketrecno}-${r.tvrecno ?? i}`} className="border-t border-rule">
                            <td className={td}>{str(r.ticketnum)}</td>
                            <td className={`${td} whitespace-nowrap`}>{shortDate(r.ticketstartdate)}</td>
                            <td className={td}>{str(r.violation)}</td>
                            <td className={td}>{str(r.violationdesc)}</td>
                            <td className={td}>{str(r.violationunit)}</td>
                            <td className={td}>{str(r.violationoos)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Block>

              <Block title="Repairs & Maintenance">
                {detail.repairs.length === 0 ? (
                  <div className="py-2 text-center text-sm text-muted">No repairs logged for this report.</div>
                ) : (
                  <ul className="divide-y divide-rule text-sm">
                    {detail.repairs.map((r) => (
                      <li key={r.recno} className="py-1.5">
                        <div className="flex flex-wrap items-baseline gap-3">
                          <span className="whitespace-nowrap font-medium">{shortDate(r.repairdate)}</span>
                          <span className="whitespace-pre-wrap">{str(r.repaircomments)}</span>
                        </div>
                        <Docs tablename="maintrepair" tablerecno={r.recno} label="Documents" />
                      </li>
                    ))}
                  </ul>
                )}
              </Block>

              <Block
                title="Inspection Defects"
                actions={canEdit && !isNew && !isDeleted ? (
                  <div className="flex items-center gap-2">
                    {defectsSaved && <span role="status" className="text-xs text-muted">{defectsSaved}</span>}
                    <button type="button" onClick={handleSaveDefects} disabled={savingDefects || !checklistDirty}
                      className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400">
                      {savingDefects ? 'Saving…' : 'Save Defects'}
                    </button>
                  </div>
                ) : undefined}
              >
                {defectsError && <div role="alert" className="mb-2 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{defectsError}</div>}
                {isNew ? (
                  <div className="py-2 text-center text-sm text-muted">The checklist is seeded when the report is created.</div>
                ) : detail.defects.length === 0 ? (
                  <div className="py-2 text-center text-sm text-muted">No defect names are configured.</div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {detail.defects.map((s) => (
                      <fieldset key={s.maintsection || '(none)'} className="rounded-md border border-rule p-2">
                        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{s.maintsection || 'Other'}</legend>
                        <ul className="space-y-1.5">
                          {s.defects.map((d) => {
                            const cur = defects[d.defectrecno] ?? { isdefect: 'N', defectcomments: '' };
                            const set = (patch: Partial<typeof cur>) => { setDefects((p) => ({ ...p, [d.defectrecno]: { ...cur, ...patch } })); setDefectsSaved(''); };
                            return (
                              <li key={d.defectrecno} className="text-sm">
                                <div className="flex items-start gap-2">
                                  <PermissionAwareInput type="checkbox" aria-label={`Defect ${d.name}`} checked={cur.isdefect === 'Y'}
                                    className="mt-1 h-4 w-4 rounded border-rule-strong" onChange={(e) => set({ isdefect: e.target.checked ? 'Y' : 'N' })} />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                      <span className={cur.isdefect === 'Y' ? 'font-medium text-danger-fg' : ''}>{d.name}</span>
                                      {d.defectdetails && <span className="text-xs text-muted">{d.defectdetails}</span>}
                                    </div>
                                    <PermissionAwareInput aria-label={`Comment ${d.name}`} className={`${inputClass} mt-0.5`} value={cur.defectcomments}
                                      placeholder="Comments…" onChange={(e) => set({ defectcomments: e.target.value })} />
                                    {d.inspectiondefectrecno ? <Docs tablename="inspectiondefect" tablerecno={d.inspectiondefectrecno} label="Documents" /> : null}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </fieldset>
                    ))}
                  </div>
                )}
              </Block>

              <Block title="Corrections">
                {detail.corrections.length === 0 ? (
                  <div className="py-2 text-center text-sm text-muted">No correction notes.</div>
                ) : (
                  <ul className="divide-y divide-rule text-sm">
                    {detail.corrections.map((n) => (
                      <li key={n.recno} className="py-1.5">
                        <div className="text-xs text-muted">{`${fmtDateTime(n.datetime)} · ${n.userid}`}</div>
                        <div className="whitespace-pre-wrap">{n.notes}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Block>
            </div>
          </PermissionAccessProvider>
        )}

        {!loading && detail && activeTab === 'history' && recno !== null && (
          <SafetyHistoryTab recno={recno} fetch={safetySystemService.getMaintenanceHistory} labels={HISTORY_LABELS} emptyText="No history for this report." />
        )}
      </div>
    </Drawer>
  );
}
